// Phase 26.1.1 — reusable left-sticky section navigation for tall drawers.
// Pattern used by professional CRMs (HubSpot, Salesforce): the drawer holds
// a fixed-width vertical nav on the left and a scrollable content column on
// the right. Clicking a nav item smooth-scrolls the matching section into
// view; the active item follows the reader via IntersectionObserver.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * @param {object} props
 * @param {Array<{id: string, label: string, icon?: React.ComponentType, badge?: React.ReactNode, hidden?: boolean}>} props.sections
 * @param {string} [props.testId]
 * @param {React.ReactNode} props.children  Sections rendered by the caller — each MUST wrap in <DrawerSection id="…" />
 */
export default function DrawerSectionNav({ sections, testId = 'drawer-section-nav', children }) {
  const scrollRef = useRef(null);
  const suppressObserverRef = useRef(false);
  const [activeId, setActiveId] = useState(sections.find((s) => !s.hidden)?.id);

  const visibleSections = useMemo(
    () => sections.filter((s) => !s.hidden),
    [sections],
  );

  // Track which section is currently visible in the scroll container.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return undefined;
    const nodes = visibleSections
      .map((s) => root.querySelector(`[data-drawer-section="${s.id}"]`))
      .filter(Boolean);
    if (nodes.length === 0) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        // Suspend observer updates while a manual click-scroll is in flight —
        // otherwise the smooth scroll would flip active state through every
        // section it passes.
        if (suppressObserverRef.current) return;
        const inView = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (inView[0]) {
          setActiveId(inView[0].target.getAttribute('data-drawer-section'));
        }
      },
      {
        root,
        // Widened band so shorter sections still register while giving the
        // top ~15% of the viewport priority as the "active reading zone".
        rootMargin: '-15% 0px -50% 0px',
        threshold: [0, 0.1, 0.5, 1],
      },
    );
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, [visibleSections]);

  const scrollTo = (id) => {
    const root = scrollRef.current;
    if (!root) return;
    const target = root.querySelector(`[data-drawer-section="${id}"]`);
    if (!target) return;
    // Snap active state to the clicked id and suspend the observer until the
    // smooth scroll animation has settled.
    setActiveId(id);
    suppressObserverRef.current = true;
    root.scrollTo({ top: target.offsetTop - 8, behavior: 'smooth' });
    window.setTimeout(() => { suppressObserverRef.current = false; }, 600);
  };

  return (
    <div className="flex gap-0 h-full min-h-0" data-testid={testId}>
      {/* Left sticky nav */}
      <aside
        className="w-48 shrink-0 border-r border-slate-200 bg-slate-50/70 py-4 overflow-y-auto"
        data-testid={`${testId}-list`}
      >
        <nav className="flex flex-col gap-0.5 px-2">
          {visibleSections.map((s) => {
            const Icon = s.icon;
            const isActive = s.id === activeId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => scrollTo(s.id)}
                className={cn(
                  'group relative flex items-center gap-2 rounded-md px-3 py-2 text-left text-xs font-medium transition',
                  isActive
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/60',
                )}
                data-testid={`${testId}-item-${s.id}`}
                data-active={isActive ? 'true' : 'false'}
              >
                {isActive && (
                  <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-sky-500" aria-hidden />
                )}
                {Icon ? <Icon size={14} className="shrink-0 opacity-70" /> : null}
                <span className="truncate">{s.label}</span>
                {s.badge != null && (
                  <span className="ml-auto shrink-0">{s.badge}</span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Scrollable content */}
      <div
        ref={scrollRef}
        className="flex-1 min-w-0 overflow-y-auto"
        data-testid={`${testId}-content`}
      >
        <div className="px-6 py-4 space-y-8 pb-24">{children}</div>
      </div>
    </div>
  );
}

/** Convenience wrapper — every section rendered inside DrawerSectionNav MUST
 * declare its id via this component so the nav can observe/scroll it. */
export function DrawerSection({ id, title, description, children }) {
  return (
    <section data-drawer-section={id} className="scroll-mt-4">
      {(title || description) && (
        <header className="mb-3">
          {title && (
            <h3 className="text-sm font-semibold text-slate-900 tracking-tight">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
          )}
        </header>
      )}
      <div className="space-y-4">{children}</div>
    </section>
  );
}
